import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "@fmc-digital/i18n";
import { MFACodeContainer } from "../../../components/organisms/MFACodeContainer/MFACodeContainer";
import { RegisterContext } from "@profile/contexts/RegisterContext";
import { triggerOtp } from "@fmc-digital/auth-api-generated/src/api/fmc/unauth/customer/triggerOtp";
import { validateOtp } from "@fmc-digital/auth-api-generated/src/api/fmc/unauth/customer/validateOtp";
import {
  extractApiErrorCode,
  getRegisterErrorMessageFromCode,
  handleRegisterOtpValidationError,
  type RegisterOtpValidationResult,
} from "@profile/features/registration/registerErrorMessage";
import { RegistrationEvent } from "@profile/features/registration/analytics";
import { useRegistrationProvider } from "@profile/providers/RegistrationProvider";

interface PhoneOtpStepProps {
  setStatusMessage: (msg: string | null) => void;
}

interface RegisterPhoneTriggerOtpRequest {
  factor: {
    factorType: "PHONE";
    phoneNumber: string;
    id: string;
  };
  existingFactor: boolean;
}

export function PhoneOtpStep({ setStatusMessage }: PhoneOtpStepProps) {
  const { t, i18n } = useTranslation();
  const { config, trackRegistrationEvent } = useRegistrationProvider();
  const {
    phone,
    otpId,
    otpFactorId,
    phoneFactorId,
    setPhoneFactorId,
    setOtpId,
    setOtp,
    goNext,
    goBack,
  } = useContext(RegisterContext);
  const [otpError, setOtpError] = useState<string | null>(null);
  const initialOtpRequestedRef = useRef(false);
  const triggerFactorIdRef = useRef("");

  const translate = (key: string) => t(key as never) as string;
  const normalizedPhone = useMemo(() => phone.replace(/\D/g, ""), [phone]);

  const trackPhoneOtpValidationFailed = useCallback((
    result: RegisterOtpValidationResult | undefined,
    errorCode: string | null,
  ) => {
    if (
      result?.status === "CLEAR_CODE" ||
      result?.status === "ALREADY_VALIDATED" ||
      result?.status === "BANNER_ONLY"
    ) {
      return;
    }
    try {
      trackRegistrationEvent(RegistrationEvent.PHONE_OTP_VALIDATION_FAILED, {
        ...(errorCode ? { errorCode } : {}),
      });
    } catch {
      // Analytics must not block flow
    }
  }, [trackRegistrationEvent]);

  const triggerPhoneOtp = useCallback(async () => {
    setStatusMessage(null);
    let prevBaseUrl: string | undefined = undefined;

    if (!triggerFactorIdRef.current) {
      triggerFactorIdRef.current = otpId || "";
    }

    try {
      const baseUrlV2 = config.apiBaseUrlV2 || config.apiBaseUrl;
      if (typeof window !== "undefined" && window.__authShellConfig) {
        prevBaseUrl = window.__authShellConfig.apiBaseUrl;
        window.__authShellConfig.apiBaseUrl = `${baseUrlV2}/unauth`;
      }

      const payload: RegisterPhoneTriggerOtpRequest = {
        factor: {
          factorType: "PHONE",
          phoneNumber: normalizedPhone,
          id: triggerFactorIdRef.current,
        },
        existingFactor: false,
      };

      const response = await triggerOtp(payload);

      if (response?.id && response?.factorId) {
        setOtpId(response.id);
        setPhoneFactorId(response.factorId);
      } else {
        throw new Error("register:otp_trigger_failed");
      }
    } catch (err) {
      const message = getRegisterErrorMessageFromCode(
        err,
        translate,
        i18n.exists.bind(i18n),
      );
      setStatusMessage(message);
      throw err;
    } finally {
      if (typeof window !== "undefined" && window.__authShellConfig && prevBaseUrl !== undefined) {
        window.__authShellConfig.apiBaseUrl = prevBaseUrl;
      }
    }
  }, [config, i18n, normalizedPhone, otpId, setOtpId, setPhoneFactorId, setStatusMessage, translate]);

  useEffect(() => {
    if (phoneFactorId || initialOtpRequestedRef.current) return;

    initialOtpRequestedRef.current = true;
    let mounted = true;

    const sendPhoneOtp = async () => {
      try {
        await triggerPhoneOtp();
        if (!mounted) return;
        try {
          trackRegistrationEvent(RegistrationEvent.PHONE_OTP_REQUEST_SENT);
        } catch {
          // Analytics must not block flow
        }
      } catch {
        if (!mounted) return;
        initialOtpRequestedRef.current = false;
      }
    };

    sendPhoneOtp();

    return () => {
      mounted = false;
    };
  }, [phoneFactorId, triggerPhoneOtp]);

  const factorState = useMemo(
    () => ({
      factorType: "sms" as const,
      profile: { phoneNumber: normalizedPhone },
    }),
    [normalizedPhone],
  );

  const handleValidate = useCallback(async (code: string) => {
    setStatusMessage(null);
    setOtpError(null);

    const factorId = phoneFactorId || otpFactorId;
    if (!otpId || !factorId) {
      setOtpError("Something went wrong.");
      return null;
    }

    try {
      trackRegistrationEvent(RegistrationEvent.PHONE_OTP_SUBMITTED);
    } catch {
      // Analytics must not block validation
    }

    try {
      return await validateOtp({
        factorValue: code,
        id: otpId,
        factorId,
      });
    } catch (err) {
      const errorCode = extractApiErrorCode(err);
      const message = getRegisterErrorMessageFromCode(
        err,
        translate,
        i18n.exists.bind(i18n),
      );

      const result = handleRegisterOtpValidationError(
        errorCode,
        message,
        setStatusMessage,
        setOtpError,
      );
      trackPhoneOtpValidationFailed(result, errorCode);
      return result;
    }
  }, [
    i18n,
    otpFactorId,
    otpId,
    phoneFactorId,
    setStatusMessage,
    trackPhoneOtpValidationFailed,
    trackRegistrationEvent,
    translate,
  ]);

  const handleVerify = useCallback((verifiedCode: string) => {
    setStatusMessage(null);
    try {
      trackRegistrationEvent(RegistrationEvent.PHONE_OTP_VALIDATION_SUCCESS);
    } catch {
      // Analytics must not block navigation
    }
    setOtp(verifiedCode);
    goNext();
  }, [goNext, setOtp, setStatusMessage, trackRegistrationEvent]);

  const handleInputEdit = useCallback(() => {
    if (otpError) setOtpError(null);
    setStatusMessage(null);
  }, [otpError, setStatusMessage]);

  const handleCancel = useCallback(() => {
    setOtpError(null);
    setStatusMessage(null);
    goBack();
  }, [goBack, setStatusMessage]);

  const handleResend = useCallback(async () => {
    setOtpError(null);
    await triggerPhoneOtp();
  }, [triggerPhoneOtp]);

  return (
    <MFACodeContainer
        factorState={factorState}
        onVerify={handleVerify}
        onCancel={handleCancel}
        onValidate={handleValidate}
        onResend={handleResend}
        inputError={otpError}
        onInputEdit={handleInputEdit}
    />
  );
}
